import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Link } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { setAnalyticsCollectionEnabled } from 'firebase/analytics';
import { analytics } from '../../firebaseConfig';

const CONSENT_KEY = 'ntc:cookie-consent';

/**
 * Lightweight cookie consent banner.
 * Controls Firebase Analytics collection based on user choice.
 * Essential cookies (Firebase Auth) are always enabled.
 */
const CookieConsent = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem(CONSENT_KEY);
    if (consent === null) {
      // No consent recorded yet — disable analytics until user decides
      if (analytics) {
        setAnalyticsCollectionEnabled(analytics, false);
      }
      setVisible(true);
    } else if (consent === 'declined') {
      // User previously declined — keep analytics disabled
      if (analytics) {
        setAnalyticsCollectionEnabled(analytics, false);
      }
    }
    // If consent === 'accepted', analytics stays enabled (default)
  }, []);

  const handleAccept = () => {
    localStorage.setItem(CONSENT_KEY, 'accepted');
    if (analytics) {
      setAnalyticsCollectionEnabled(analytics, true);
    }
    setVisible(false);
  };

  const handleDecline = () => {
    localStorage.setItem(CONSENT_KEY, 'declined');
    if (analytics) {
      setAnalyticsCollectionEnabled(analytics, false);
    }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1400,
        bgcolor: 'var(--bg-secondary, #1a1a2e)',
        borderTop: '1px solid var(--border-color, #333)',
        px: 3,
        py: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 2,
      }}
    >
      <Typography variant="body2" sx={{ flex: 1, minWidth: 280, color: 'var(--text-secondary, #ccc)' }}>
        We use essential cookies for authentication and optional analytics cookies (Firebase Analytics) to understand how the service is used.
        See our <Link to="/privacy" component={RouterLink} sx={{ color: 'var(--accent-color, #64b5f6)' }}>Privacy Policy</Link> for details.
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
        <Button
          variant="outlined"
          size="small"
          onClick={handleDecline}
          sx={{ textTransform: 'none' }}
        >
          Decline Analytics
        </Button>
        <Button
          variant="contained"
          size="small"
          onClick={handleAccept}
          sx={{ textTransform: 'none' }}
        >
          Accept All
        </Button>
      </Box>
    </Box>
  );
};

export default CookieConsent;
